import { Select, type UseFormReturn } from '@faceup/form'
import { FormItemType } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useMemo } from 'react'
import { sharedMessages } from '../../../Shared/translations'
import { defineMessages, useIntl } from '../../../TypedIntl'

const messages = defineMessages({
  categoryLabel: 'Shared.report.category',
})

type Category = {
  id: string
  name: string
}

type CategoryItem = {
  id: string
  isRequired?: boolean
  labelTranslation?: { translation?: string | null } | null
  hintTranslation?: { translation?: string | null } | null
}

type UseAF_CategorySelectParams = {
  categoryItem: CategoryItem | undefined
  categories: Category[]
  selectedCategory: string | null
  formItemsLoading: boolean
  onCategoryChange: (categoryId: string) => void
}

export const useAF_CategorySelect = ({
  categoryItem,
  categories,
  selectedCategory,
  formItemsLoading,
  onCategoryChange,
}: UseAF_CategorySelectParams) => {
  const { formatMessage } = useIntl()

  return useMemo(() => {
    if (!categoryItem) {
      return null
    }

    const categorySchema = yup.object({
      type: yup.string().oneOf([FormItemType.Category]).required(),
      value: categoryItem.isRequired
        ? yup
            .string()
            .required()
            .oneOf(categories.map(c => c.id))
        : yup.string().oneOf(categories.map(c => c.id)),
    })

    return {
      id: categoryItem.id,
      schema: categorySchema,
      defaultValue: {
        type: FormItemType.Category,
        value:
          categories.length === 1
            ? (categories[0]?.id ?? selectedCategory)
            : (selectedCategory ?? undefined),
      },
      render: (form: UseFormReturn) => (
        <div className='flex flex-col gap-4'>
          <Select
            control={form.control}
            name={`${categoryItem.id}.value`}
            label={
              categoryItem.labelTranslation?.translation ?? formatMessage(messages.categoryLabel)
            }
            hint={categoryItem.hintTranslation?.translation ?? undefined}
            withAsterisk={categoryItem.isRequired ?? false}
            placeholder={formatMessage(sharedMessages.selectPlaceholder)}
            loading={formItemsLoading}
            data-test='create-company-report-form-category-input'
            popupRender={menu => (
              <div data-test='create-company-report-form-category-menu'>{menu}</div>
            )}
            className='w-full'
            options={categories.map(category => ({
              value: category.id,
              label: category.name,
              'data-test': 'create-company-report-form-category-item',
            }))}
            onChange={(value: string | null) => {
              if (value) {
                onCategoryChange(value)
              }
            }}
            formItemProps={{
              'data-test': 'create-company-report-form-category',
            }}
          />
        </div>
      ),
    }
  }, [
    categoryItem,
    categories,
    selectedCategory,
    formItemsLoading,
    onCategoryChange,
    formatMessage,
  ])
}
